/*
 * Copyright (c) CIRROM 2025.
 */

import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { IntegratedSystem } from "../api/integratedsystems";
import { DataProject } from "../api/dataprojects";

interface SystemsAndProjectsContextType {
  integratedSystems: IntegratedSystem[];
  dataProjects: DataProject[];
  selectedSystemId?: number;
  setSelectedSystemId: (id: number) => void;
  fetchDataProjects: () => void;
}

const SystemsAndProjectsContext = createContext<
  SystemsAndProjectsContextType | undefined
>(undefined);

interface SystemsAndProjectsProviderProps
  extends SystemsAndProjectsContextType {
  children: ReactNode;
}

export function SystemsAndProjectsProvider({
  children,
  integratedSystems,
  dataProjects,
  selectedSystemId,
  setSelectedSystemId,
  fetchDataProjects,
}: SystemsAndProjectsProviderProps) {
  const value = useMemo(
    () => ({
      integratedSystems,
      dataProjects,
      selectedSystemId,
      setSelectedSystemId,
      fetchDataProjects,
    }),
    [
      integratedSystems,
      dataProjects,
      selectedSystemId,
      setSelectedSystemId,
      fetchDataProjects,
    ]
  );

  return (
    <SystemsAndProjectsContext.Provider value={value}>
      {children}
    </SystemsAndProjectsContext.Provider>
  );
}

export function useSystemsAndProjects() {
  const context = useContext(SystemsAndProjectsContext);
  if (!context) {
    throw new Error(
      "useSystemsAndProjects must be used within SystemsAndProjectsProvider"
    );
  }
  return context;
}

/**
 * The same context for callers that only decorate with it. A component that
 * merely wants a system's vendor should not become unmountable outside the
 * provider - it renders its neutral form instead, which is what the review
 * surfaces do under test and anywhere the provider has not loaded yet.
 */
export function useOptionalSystemsAndProjects() {
  return useContext(SystemsAndProjectsContext);
}
